/* eslint-disable consistent-return */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useForm } from 'react-hook-form';

import useAxios from '../../../hooks/useAxios';
import SpinnerOverlay from '../../../components/SpinnerOverlay/SpinnerOverlay';
import { customTableSortIcon, handleError } from '../../../utils';
import './DueDateManagement.scss';
import EditIcon from '../../../assets/icons/edit-icon.svg';
import DeleteIcon from '../../../assets/icons/delete-icon.svg';
import EyeIcon from '../../../assets/icons/eye-icon.svg';

const DueDateManagement = () => {
  const toast = useRef(null);
  const toastBottom = useRef(null);

  const navigate = useNavigate();

  const { get, post, apiLoading } = useAxios();

  const defaultOptions = {
    defaultStd: 2,
    defaultCpt: 2,
    fastrackStd: 1,
    fastrackCpt: 1
  };

  const [allDueDates, setAllDueDates] = useState([]);
  const [defaultStd, setDefaultStd] = useState(0);
  const [defaultCpt, setDefaultCpt] = useState(0);
  const [fastrackStd, setFastrackStd] = useState(0);
  const [fastrackCpt, setFastrackCpt] = useState(0);
  const [prevDefaultStd, setPrevDefaultStd] = useState(0);
  const [prevDefaultCpt, setPrevDefaultCpt] = useState(0);
  const [prevFastrackStd, setPrevFastrackStd] = useState(0);
  const [prevFastrackCpt, setPrevFastrackCpt] = useState(0);
  const [autoCloseReopenedStd, setAutoCloseReopenedStd] = useState(0);
  const [prevAutoCloseReopenedStd, setPrevAutoCloseReopenedStd] = useState(0);

  const {
    control,
    formState: { errors, isSubmitting, isDirty, isValid },
    handleSubmit,
    watch,
    setValue
  } = useForm({ defaultOptions });

  const onEditDueDate = (rowData: any) => {
    navigate(`/settings/due-date-management/edit/${rowData.id}`, { state: { ...rowData, isView: false } });
  };

  const onViewDueDate = (rowData: any) => {
    navigate(`/settings/due-date-management/edit/${rowData.id}`, { state: { ...rowData, isView: true } });
  };

  const workflowTypes = [{ label: 'Standard', value: 'standard' }, { label: 'Collaboration', value: 'concept' }];

  const getAllDueDates = async () => {
    const apiPath = 'workflow-manager/get_due_date_configuration_rules';

    await get(apiPath).then((response: any) => {
      const { status } = response;

      if ([200, 201].includes(status)) {
        if (response.data.messages[0] && response.data.messages[0].custom_rules && response.data.messages[0].custom_rules.length > 0) {
          setAllDueDates(response.data.messages[0].custom_rules.map((item: any) => {
            item.workflow_type = workflowTypes.find((val: any) => val.value === item.workflow_type)?.label;
            item.file_type = item.file_type.toUpperCase();
            item.large_assets = `${item.unit_value} ${item.unit_label}`;
            return item;
          }));
        }
        setDefaultStd(response.data.messages[0].default_rules.find((val: any) => val.workflow_type === 'standard').default);
        setFastrackStd(response.data.messages[0].default_rules.find((val: any) => val.workflow_type === 'standard').fast_track);
        setDefaultCpt(response.data.messages[0].default_rules.find((val: any) => val.workflow_type === 'concept').default);
        setFastrackCpt(response.data.messages[0].default_rules.find((val: any) => val.workflow_type === 'concept').fast_track);
        setAutoCloseReopenedStd(response.data.messages[0].default_rules.find((val: any) => val.workflow_type === 'standard').auto_close ?? 0);

        setPrevDefaultStd(response.data.messages[0].default_rules.find((val: any) => val.workflow_type === 'standard').default);
        setPrevFastrackStd(response.data.messages[0].default_rules.find((val: any) => val.workflow_type === 'standard').fast_track);
        setPrevDefaultCpt(response.data.messages[0].default_rules.find((val: any) => val.workflow_type === 'concept').default);
        setPrevFastrackCpt(response.data.messages[0].default_rules.find((val: any) => val.workflow_type === 'concept').fast_track);
        setPrevAutoCloseReopenedStd(response.data.messages[0].default_rules.find((val: any) => val.workflow_type === 'standard').auto_close ?? 0);
      }
    })
      .catch((error: any) => {
        if ([401, 404, 500].includes(error?.request?.status)) {
          handleError(error, toastBottom);
        }
      });
  };

  const deleteDueDate = async (rowData: any) => {
    if (rowData && rowData.id) {
      const postData = {
        rule_id: rowData.id
      };

      await post('workflow-manager/delete_existing_due_date_rule', postData).then((response: any) => {
        const { status } = response;

        if ([200, 201].includes(status) && response.data.messages[0]) {
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'Deleted the Due Date successfully!' });
          getAllDueDates();
        } else if (response.data.errors[0]) {
          toastBottom.current.show({ severity: 'error', summary: 'Error', detail: response.data.errors[0] });
        }
      })
        .catch((error: any) => {
          if ([401, 404, 500].includes(error?.request?.status)) {
            handleError(error, toastBottom);
          }
        });
    }
  };

  const deleteDueDateConfirm = (rowData: any) => {
    confirmDialog({
      message: 'Are you sure you want to delete this Due Date?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      rejectClassName: 'outline-btn-compact',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      accept: () => {
        deleteDueDate(rowData);
      },
      reject: () => {
        toast.current.show({ severity: 'warn', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
      }
    });
  };

  const actionColumnTemplate = (rowData: any) => {
    return (
      <div className="action-col">
        <img
          src={EyeIcon}
          title="View"
          alt="View"
          width="18"
          style={{ marginRight: 10, cursor: 'pointer' }}
          onClick={() => onViewDueDate(rowData)}
        />
        <img src={EditIcon} style={{ marginRight: 10, cursor: 'pointer' }} alt="Edit" onClick={() => onEditDueDate(rowData)} />
        <img src={DeleteIcon} alt="Delete" style={{ cursor: 'pointer' }} onClick={() => deleteDueDateConfirm(rowData)} />
      </div>
    );
  };

  function isValueChanged(): boolean {
    return !(defaultStd !== prevDefaultStd || defaultCpt !== prevDefaultCpt || fastrackStd !== prevFastrackStd || fastrackCpt !== prevFastrackCpt || autoCloseReopenedStd !== prevAutoCloseReopenedStd);
  }

  function revertChanges() {
    setDefaultStd(prevDefaultStd);
    setFastrackStd(prevFastrackStd);
    setDefaultCpt(prevDefaultCpt);
    setFastrackCpt(prevFastrackCpt);
    setAutoCloseReopenedStd(prevAutoCloseReopenedStd);
  }

  const onSubmit = async () => {
    const reqObj = {
      default_due_date_rules: [
        {
          workflow_type: 'standard',
          default: defaultStd,
          fast_track: fastrackStd,
          auto_close: autoCloseReopenedStd
        },
        {
          workflow_type: 'concept',
          default: defaultCpt,
          fast_track: fastrackCpt
        }
      ]
    };

    await post('workflow-manager/update_default_due_date_rules', reqObj).then((response: any) => {
      const { status } = response;

      if ([200, 201].includes(status)) {
        if (response.data.messages[0]) {
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'Configurations updated successfully!', life: 3000 });
          setTimeout(() => {
            getAllDueDates();
          }, 300);
        } else if (response.data.errors[0]) {
          toastBottom.current.show({ severity: 'error', summary: 'Error', detail: response.data.errors[0] });
        }
      }
    })
      .catch((error: any) => {
        if ([401, 404, 500].includes(error?.request?.status)) {
          handleError(error, toastBottom);
        }
      });
  };

  useEffect(() => {
    getAllDueDates();
  }, []);

  const header = (
    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
      <span className="table-header-title">Custom Due Date</span>
      <Button icon="pi pi-plus" rounded raised label="Add New Due Date" onClick={() => navigate('/settings/due-date-management/add')} />
    </div>
  );

  return (
    <>
      <div role="dueDatemanagement" className="dueDatemanagement-container">
        <Toast ref={toast} />
        <Toast ref={toastBottom} position="bottom-right" />

        <div className="flex p-3 btm-btn-container flex-wrap align-items-center justify-content-between gap-2">
          <span className="table-header-title">Default Due Date</span>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="default-due-date-form-inner">
          <div className="pl-4 flex basic-config-details-header">
            <div className="col-6">Standard Workflow</div>
            <div className="col-6">Collaboration Workflow</div>
          </div>
          <div className="pl-4 flex basic-config-details">
            <div className="col-3">Default</div>
            <div className="col-3">Fast Track</div>
            <div className="col-3">Default</div>
            <div className="col-3">Fast Track</div>
          </div>
          <div className="pl-4 flex basic-config-details">
            <div className="col-3">
              <InputNumber
                style={{ width: '85%' }}
                className="cus-input-btn"
                showButtons
                min={1}
                value={defaultStd}
                placeholder="Enter Default Standard"
                onValueChange={(e) => setDefaultStd(e.value)}
              />
            </div>
            <div className="col-3">
              <InputNumber
                style={{ width: '85%' }}
                className="cus-input-btn"
                showButtons
                min={1}
                value={fastrackStd}
                placeholder="Enter Fastrack Standard"
                onValueChange={(e) => setFastrackStd(e.value)}
              />
            </div>
            <div className="col-3">
              <InputNumber
                style={{ width: '85%' }}
                className="cus-input-btn"
                showButtons
                min={1}
                value={defaultCpt}
                placeholder="Enter Default Collaboration"
                onValueChange={(e) => setDefaultCpt(e.value)}
              />
            </div>
            <div className="col-3">
              <InputNumber
                style={{ width: '85%' }}
                className="cus-input-btn"
                showButtons
                min={1}
                value={fastrackCpt}
                placeholder="Enter Fastrack Collaboration"
                onValueChange={(e) => setFastrackCpt(e.value)}
              />
            </div>
          </div>

          <div className="flex p-3 btm-btn-container flex-wrap align-items-center justify-content-between gap-2">
            <span className="table-header-title">Auto-Close Reopened Workflow</span>
          </div>
          <div className="pl-4 flex basic-config-details-header">
            <div className="col-6">Standard Workflow</div>
          </div>
          <div className="pl-4 flex basic-config-details">
            <div className="col-3">Default</div>
          </div>
          <div className="pl-4 flex basic-config-details">
            <div className="col-3">
              <InputNumber
                style={{ width: '85%' }}
                className="cus-input-btn"
                showButtons
                min={1}
                value={autoCloseReopenedStd}
                placeholder="Enter Default Standard"
                onValueChange={(e) => setAutoCloseReopenedStd(e.value ?? 0)}
              />
            </div>
          </div>

          <div className="w-full mt-0 flex btm-btn-container justify-content-end">
            <Button className="outline-btn mr-2" type="button" outlined label="Cancel" onClick={() => { revertChanges(); }} />
            <Button
              type="submit"
              rounded
              raised
              label="Save"
              iconPos="right"
              disabled={isValueChanged()}
            />
          </div>
        </form>
      </div>

      <div role="groupmanagement" className="groupmanagement-container">
        <ConfirmDialog />
        {apiLoading && (
          <div className="card">
            <SpinnerOverlay />
          </div>
        )}
        <div className="card">
          <DataTable
            value={allDueDates}
            header={header}
            tableStyle={{ minWidth: '50rem' }}
            className="compact-datatable"
            sortField="workflow_type"
            sortMode="single"
            sortIcon={customTableSortIcon}
            emptyMessage="No record found"
          >
            <Column field="workflow_type" header="Workflow Type" sortable />
            <Column field="file_type" header="File Type" sortable />
            <Column field="large_assets" header="Large Assets" sortable />
            <Column field="business_days" header="Configurable Due Days(Business Days)" sortable />
            <Column field="id" header="Action" body={actionColumnTemplate} />
          </DataTable>
        </div>
      </div>
    </>
  );
};

export default DueDateManagement;
